/** @typedef {import('../models/registryTypes').RegistryEntry} RegistryEntry */
/** @typedef {import('../models/registryTypes').RoomMember} RoomMember */

/**
 * Prefer the earlier row when two identities collide; keep active over inactive.
 * @param {RegistryEntry} a
 * @param {RegistryEntry} b
 */
function preferRegistryEntry(a, b) {
  const aActive = a.active !== false;
  const bActive = b.active !== false;
  if (aActive && !bActive) return a;
  if (bActive && !aActive) return b;
  const aAt = Number(a.addedAt) || 0;
  const bAt = Number(b.addedAt) || 0;
  if (aAt <= bAt) {
    return {
      ...b,
      ...a,
      id: a.id,
      active: aActive || bActive,
      displayName: a.displayName || b.displayName,
      avatar: a.avatar || b.avatar,
      userId: a.userId || b.userId,
    };
  }
  return {
    ...a,
    ...b,
    id: b.id,
    active: aActive || bActive,
    displayName: b.displayName || a.displayName,
    avatar: b.avatar || a.avatar,
    userId: b.userId || a.userId,
  };
}

/**
 * Collapse duplicate linked rows (same userId / playerId) created by dual shell auto-register.
 * Manual guests without userId stay distinct by entry id.
 * @param {RegistryEntry[]} entries
 * @returns {RegistryEntry[]}
 */
export function dedupeRegistryEntriesByIdentity(entries) {
  /** @type {Map<string, RegistryEntry>} */
  const byKey = new Map();
  /** @type {RegistryEntry[]} */
  const anonymous = [];

  for (const e of entries || []) {
    if (!e?.id) continue;
    const key = e.userId
      ? `u:${e.userId}`
      : e.playerId
        ? `p:${e.playerId}`
        : null;
    if (!key) {
      anonymous.push(e);
      continue;
    }
    const prev = byKey.get(key);
    byKey.set(key, prev ? preferRegistryEntry(prev, e) : e);
  }

  return [...byKey.values(), ...anonymous];
}

/**
 * Union roster rows by id (client overlays server) — prevents joiners from wiping the room roster.
 * @param {RegistryEntry[]} serverEntries
 * @param {RegistryEntry[]} clientEntries
 */
export function mergeRegistryEntryLists(serverEntries, clientEntries) {
  /** @type {Map<string, RegistryEntry>} */
  const map = new Map();
  for (const e of serverEntries || []) {
    if (e?.id) map.set(e.id, e);
  }
  for (const e of clientEntries || []) {
    if (!e?.id) continue;
    const prev = map.get(e.id);
    map.set(e.id, prev ? { ...prev, ...e } : e);
  }
  return dedupeRegistryEntriesByIdentity(Array.from(map.values()));
}

/**
 * @param {RoomMember[]} serverMembers
 * @param {RoomMember[]} clientMembers
 */
export function mergeRoomMemberLists(serverMembers, clientMembers) {
  /** @type {Map<string, RoomMember>} */
  const byUser = new Map();
  const withoutUser = [];
  for (const m of serverMembers || []) {
    if (m?.userId) byUser.set(m.userId, m);
    else if (m?.id) withoutUser.push(m);
  }
  for (const m of clientMembers || []) {
    if (!m?.userId) continue;
    const prev = byUser.get(m.userId);
    byUser.set(m.userId, prev ? { ...prev, ...m } : m);
  }
  return [...withoutUser, ...Array.from(byUser.values())];
}
